class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.recommendationsContainer = document.getElementById('cart-notification-recommendations');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.complementaryRequestId = 0;
    this.complementaryAbortController = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
    this.resetComplementaryRecommendations();
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.resetComplementaryRecommendations();

    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();

    const lineItem = parsedState.key ? parsedState : parsedState.items?.[parsedState.items.length - 1];
    const productId = parsedState.product_id || lineItem?.product_id;
    const productHandle = CartNotification.getProductHandle(lineItem || parsedState);

    console.log('[cart-complementary] added product', { productId, productHandle });

    if (productId && this.dataset.recommendationsUrl) {
      this.loadComplementaryRecommendations(productId, productHandle);
    }
  }

  resetComplementaryRecommendations() {
    this.complementaryAbortController?.abort();
    this.complementaryAbortController = null;

    if (!this.recommendationsContainer) return;

    this.recommendationsContainer.innerHTML = '';
    this.recommendationsContainer.hidden = true;
  }

  static getProductHandle(lineItem) {
    if (!lineItem) return null;
    if (lineItem.handle) return lineItem.handle;

    const match = String(lineItem.url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static parseRecommendationsContent(html) {
    if (!html) return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#cart-notification-complementary-content');
  }

  static logRecommendationsDebug(label, content) {
    if (!content) {
      console.log(`[cart-complementary] ${label}: no content`);
      return;
    }

    const items = [...content.querySelectorAll('.cart-notification-complementary__item')].map((item) => ({
      title: item.querySelector('.card__heading')?.textContent.trim() || item.textContent.trim().slice(0, 40),
      matchSlug: item.dataset.debugMatchSlug || null,
      matchCollection: item.dataset.debugMatchCollection || null,
    }));

    console.log(`[cart-complementary] ${label}`, {
      product: content.dataset.debugProduct || null,
      source: content.dataset.debugSource || content.dataset.headingType || null,
      winningSlug: content.dataset.debugWinningSlug || null,
      winningSort: content.dataset.debugWinningSort || null,
      productCategories: content.dataset.debugCategories || null,
      itemCount: items.length,
      items,
    });
  }

  static trimRecommendationsContent(content, limit) {
    if (!content || !limit) return content;

    content.querySelectorAll('.cart-notification-complementary__item').forEach((item, index) => {
      if (index >= limit) item.remove();
    });

    return content;
  }

  fetchFallbackRecommendationsHtml(productHandle, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(productHandle)}?sections=cart-complementary-fallback`;

    return fetch(url, { signal })
      .then((response) => response.json())
      .then((sections) => sections['cart-complementary-fallback'] || null);
  }

  loadComplementaryRecommendations(productId, productHandle) {
    const requestId = ++this.complementaryRequestId;
    this.complementaryAbortController?.abort();
    this.complementaryAbortController = new AbortController();
    const { signal } = this.complementaryAbortController;
    const limit = 2;

    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', 'cart-notification-complementary');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('intent', 'complementary');

    fetch(url.toString(), { signal })
      .then((response) => response.text())
      .then(async (text) => {
        if (requestId !== this.complementaryRequestId) return;

        let content = CartNotification.parseRecommendationsContent(text);
        CartNotification.logRecommendationsDebug('Search & Discovery (complementary)', content);

        if (!content?.innerHTML.trim().length && productHandle) {
          console.log(`[cart-complementary] no complementary products, fetching fallback for "${productHandle}"`);
          const fallbackHtml = await this.fetchFallbackRecommendationsHtml(productHandle, signal);
          if (requestId !== this.complementaryRequestId) return;
          content = CartNotification.parseRecommendationsContent(fallbackHtml);
          CartNotification.logRecommendationsDebug('Metafield fallback', content);
        }

        content = CartNotification.trimRecommendationsContent(content, limit);

        if (!content?.innerHTML.trim().length) {
          this.resetComplementaryRecommendations();
          return;
        }

        this.recommendationsContainer.innerHTML = content.outerHTML;
        this.recommendationsContainer.hidden = false;
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (requestId === this.complementaryRequestId) {
          this.resetComplementaryRecommendations();
        }
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
